/**
 * The provider level of the three-level editor (ADR-040 §D-1; Flow V Frame 2).
 *
 * The topmost section of the detail pane: who this external system is, whether
 * Meridian loads it at all, and the provider-level kill switch. Its interfaces
 * are rendered *below* it by the dialog rather than inside it — deliberately, so
 * this form's Save cannot carry an interface edit and the two levels stay
 * separately audited (REQ-PROVIDER-016).
 *
 * # Delete says what goes with it
 *
 * Deleting a provider cascades to its interfaces and their connections, which is
 * materially larger than deleting one interface. The confirmation therefore
 * names the count rather than asking a generic "are you sure" — an operator who
 * has to guess the blast radius is being asked to approve something they have
 * not been told.
 *
 * # Save is not start
 *
 * As at the interface level: start/stop is a different button on a different
 * endpoint, so it lands as its own audited event and takes effect without
 * carrying whatever else is half-typed in the form. A new provider is created
 * stopped and there is no "save and start" (REQ-PROVIDER-007).
 *
 * Cross-references:
 *   - ADR-040 §D-1 / §D-3; REQ-PROVIDER-004 / -007 / -010 / -016 / -024.
 *   - REQ-UI-156 (the three levels), -158 (export states its two facts).
 *   - Flow V Frame 2, §5c, §6.
 */
import { useMemo, useState } from "react";
import { DescriptionTooltip } from "@/components/ui/tooltip";
import { PeerTextInput } from "@/peers/peer-form-primitives";
import { PeerSection } from "@/peers/peer-section";
import { StatePill, StateToggleButton } from "@/peers/peer-state-controls";
import type { InterfaceState } from "@/providers/provider-catalog";
import type { ProviderConfig } from "@/providers/provider-model";

/**
 * What stopping a provider does, disclosed from the state control.
 *
 * Architect-specified wording (2026-10-10), and a standing paragraph until
 * then. The three facts are unchanged — the stop cascades to interfaces and
 * connections, a restart resumes them as configured, and nothing accumulates
 * meanwhile — said in three short sentences instead of two long ones, and
 * behind a ``?`` rather than occupying the form permanently.
 *
 * "as configured" is doing real work: it is what tells the operator a restart
 * does not blanket-start everything, it returns each interface to its own
 * stored state.
 */
export const PROVIDER_STOP_HELP =
  "Stopping a provider stops all of its interfaces and their connections. Restarting a " +
  "stopped provider resumes all of its interfaces and their connections as configured. " +
  "While stopped, no provider data is collected or queued.";

/**
 * What an export is and what it leaves out (REQ-UI-158).
 *
 * Architect-specified wording (2026-10-10). REQ-UI-158 requires the surface
 * state both that the bundle is a projection of *current* configuration and
 * which references were omitted and why; both survive here. What is dropped
 * is the elaboration — the CA bundle's travel, the conversion-spec union, the
 * reasoning about provable publicness — which the requirement's own Notes
 * carry and an operator at the Export button does not need in order to decide
 * to click it.
 *
 * ⚠ Both Meridian references stay behind for a provider, key *and*
 * certificate, unlike the peer twin which ships its cert. That asymmetry is
 * REQ-PROVIDER-035's, so the wording says "certificate and key".
 */
export const PROVIDER_EXPORT_HELP =
  "Export downloads the provider as an installable bundle recording its current " +
  "configuration. For TLS connections, the Meridian certificate and key are excluded; " +
  "these must be supplied if the bundle is imported as a new provider.";

/** The enabled/started distinction, which the checkbox label used to carry. */
export const PROVIDER_ENABLED_HELP =
  "Enabled controls whether the service loads this provider at all. It is distinct from " +
  "started/stopped: a disabled provider is not loaded, and a loaded one can still be stopped.";

/** Props for {@link ProviderEditor}. */
export interface ProviderEditorProps {
  /** The provider being edited, as a complete PUT body. */
  value: ProviderConfig;
  /** True when this provider has never been persisted (drives copy + state control). */
  isNew: boolean;
  /** How many interfaces the delete would take with it; `null` while unknown. */
  interfaceCount: number | null;
  onSave: (config: ProviderConfig) => void;
  onDelete: () => void;
  /**
   * Download this provider as an installable bundle (REQ-UI-158).
   *
   * Absent on an unsaved provider — there is nothing on the server to project.
   */
  onExport?: () => void;
  /** True while the archive is being fetched. */
  exportPending?: boolean;
  /** Operator-facing failure text, or null. */
  exportError?: string | null;
  onSetState: (state: InterfaceState) => void;
  savePending?: boolean;
  statePending?: boolean;
  /** Server-side error text, surfaced verbatim above the buttons. */
  saveError?: string | null;
  /** The other providers' names, for the local uniqueness hint (REQ-PROVIDER-004). */
  otherNames: readonly string[];
}

/**
 * Render the provider-level form.
 *
 * ⚠ Its test ids are `provider-level-*`, not `provider-*`. The interface editor
 * renders *inside the same pane* and already owns `provider-save` /
 * `provider-delete`, so sharing them would make every selector ambiguous — and a
 * test that grabbed the wrong Save would assert a provider write against an
 * interface route.
 */
export function ProviderEditor({
  value,
  isNew,
  interfaceCount,
  onSave,
  onDelete,
  onExport,
  exportPending = false,
  exportError = null,
  onSetState,
  savePending = false,
  statePending = false,
  saveError,
  otherNames,
}: ProviderEditorProps): React.ReactElement {
  const [draft, setDraft] = useState<ProviderConfig>(value);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [seed, setSeed] = useState<ProviderConfig>(value);

  /**
   * Re-seed when the selection changes, or when a save round-trips a new
   * server-side value — including a state flip, which arrives from the
   * start/stop endpoint rather than from this form.
   *
   * ⚠ Done **during render** against a remembered seed, not in an effect. React's
   * documented "adjusting state when a prop changes" pattern: an effect would
   * render once with the stale draft, then again with the fresh one, and the
   * `react-hooks/set-state-in-effect` rule flags exactly that cascade. The egress
   * twin still uses the effect form and is carried on the remediation waiver in
   * `eslint.config.js`; that list is an inventory to shrink, so new code does not
   * join it.
   */
  if (seed !== value) {
    setSeed(value);
    setDraft(value);
    setConfirmDelete(false);
  }

  const patch = (next: Partial<ProviderConfig>): void =>
    setDraft((current) => ({ ...current, ...next }));

  const nameError = useMemo(() => {
    const trimmed = draft.name.trim();
    if (trimmed === "") return "A name is required.";
    if (otherNames.includes(trimmed)) return "Another provider already uses this name.";
    return null;
  }, [draft.name, otherNames]);

  return (
    <form
      data-testid="provider-editor"
      onSubmit={(event) => {
        event.preventDefault();
        if (nameError === null) onSave({ ...draft, name: draft.name.trim() });
      }}
    >
      <PeerSection id="provider" title="Provider">
        <div className="flex flex-col gap-3">
          <PeerTextInput
            label="Name"
            value={draft.name}
            maxLength={128}
            onChange={(name) => patch({ name })}
            error={nameError}
            testId="provider-name"
            placeholder="black-diamond-range"
            hint="Unique across the deployment. Qualifies its interfaces as provider / interface."
          />
          <PeerTextInput
            label="Description"
            value={draft.description ?? ""}
            onChange={(description) => patch({ description })}
            testId="provider-description"
            hint="What this external system is, and who operates it."
          />
          <div className="flex items-center gap-1.5">
            <label className="flex items-center gap-2 text-body-sm text-text-primary">
              <input
                type="checkbox"
                checked={draft.enabled}
                data-testid="provider-enabled"
                onChange={(event) => patch({ enabled: event.target.checked })}
                className="h-3.5 w-3.5 cursor-pointer"
              />
              Enabled
            </label>
            <DescriptionTooltip
              testId="provider-enabled"
              label="Enabled"
              description={PROVIDER_ENABLED_HELP}
            />
          </div>

          <div className="flex flex-col gap-2 border-t border-border pt-3">
            <div className="flex items-center gap-2">
              <StatePill state={draft.state} />
              {isNew ? (
                <span
                  className="text-meta text-text-muted"
                  data-testid="provider-level-state-unsaved"
                >
                  New providers are created stopped. Save first, then start it deliberately.
                </span>
              ) : (
                <StateToggleButton
                  state={draft.state}
                  pending={statePending}
                  onToggle={onSetState}
                  testId="provider-state-toggle"
                  label="provider"
                />
              )}
              {/* Beside the control it describes rather than in the section
                  header: this explains the start/stop button specifically, and
                  the section also holds identity, delete and export. */}
              <DescriptionTooltip
                testId="provider-level-state"
                label="stopping a provider"
                description={PROVIDER_STOP_HELP}
              />
            </div>
          </div>

          {saveError && (
            <p
              role="alert"
              data-testid="provider-level-save-error"
              className="text-body-sm text-destructive"
            >
              {saveError}
            </p>
          )}

          <div className="flex items-center gap-2 border-t border-border pt-3">
            {!isNew &&
              (confirmDelete ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className="text-meta text-destructive"
                    data-testid="provider-level-delete-warning"
                  >
                    Delete this provider
                    {interfaceCount !== null && interfaceCount > 0
                      ? ` and its ${interfaceCount} ${interfaceCount === 1 ? "interface" : "interfaces"}`
                      : ""}
                    ? This cannot be undone.
                  </span>
                  <button
                    type="button"
                    onClick={onDelete}
                    data-testid="provider-level-delete-confirm"
                    className="rounded bg-destructive px-2 py-0.5 text-meta text-destructive-foreground hover:opacity-90"
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    className="rounded border border-border px-2 py-0.5 text-meta text-text-primary hover:bg-bg-elevated"
                  >
                    Keep
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  data-testid="provider-level-delete"
                  className="rounded border border-destructive px-2 py-0.5 text-meta text-destructive hover:bg-destructive/10"
                >
                  Delete provider
                </button>
              ))}
            <div className="ml-auto flex items-center gap-2">
              {/* Export sits apart from Delete deliberately: one of them takes a
                  copy OUT of the deployment and the other empties it, and a row
                  that puts them side by side invites the wrong click. */}
              {onExport !== undefined && (
                <>
                  <button
                    type="button"
                    onClick={onExport}
                    disabled={exportPending}
                    data-testid="provider-level-export"
                    className="rounded border border-border px-3 py-1 text-body-sm text-text-primary hover:bg-bg-elevated disabled:opacity-50"
                  >
                    {exportPending ? "Exporting…" : "Export…"}
                  </button>
                  <DescriptionTooltip
                    testId="provider-level-export"
                    label="Export"
                    description={PROVIDER_EXPORT_HELP}
                  />
                </>
              )}
              <button
                type="submit"
                disabled={nameError !== null || savePending}
                data-testid="provider-level-save"
                className="rounded bg-brand px-3 py-1 text-body-sm font-medium text-bg-base hover:bg-brand-dim disabled:opacity-50"
              >
                {savePending ? "Saving…" : isNew ? "Create provider" : "Save provider"}
              </button>
            </div>
          </div>

          {/* REQ-UI-158's two statements — projection-not-replay, and what was
              omitted and why — moved into the Export control's own tooltip
              (:data:`PROVIDER_EXPORT_HELP`) on 2026-10-10 per architect
              direction. The requirement's "renders beside it" wording was
              amended with it; see REQ-UI-158 Verification. An export ERROR is
              not informational and stays visible. */}
          {exportError !== null && (
            <p
              role="alert"
              data-testid="provider-level-export-error"
              className="text-meta text-destructive"
            >
              {exportError}
            </p>
          )}
        </div>
      </PeerSection>
    </form>
  );
}
